import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Alert,
  Button,
  CircularProgress,
  Collapse,
  IconButton
} from '@mui/material';
import {
  CheckCircle,
  RadioButtonUnchecked,
  Error,
  ExpandMore,
  ExpandLess,
  Description,
  Lightbulb,
  Headphones,
  Storage,
  Email,
  Search
} from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { useAuthenticatedFetch } from '../../hooks/useAuthenticatedFetch';
import { createApiClient } from '../../utils/api';

interface ProcessingStatusDialogProps {
  open: boolean;
  operationId: string;
  onClose: () => void;
  onComplete: () => void;
}

interface ProcessingStep {
  name: string;
  displayName: string;
  icon: React.ReactNode;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  error?: string;
}

type StepName = 'extract_text' | 'generate_descriptions' | 'extract_key_findings' | 'generate_podcast' | 'store_data' | 'sending_notifications' | 'submitted_for_indexing';

const stepConfig: Record<StepName, { displayName: string; icon: React.ReactNode }> = {
  extract_text: {
    displayName: 'Extracting text from PDF',
    icon: <Description />
  },
  generate_descriptions: {
    displayName: 'Generating report descriptions',
    icon: <Description />
  },
  extract_key_findings: {
    displayName: 'Extracting key findings',
    icon: <Lightbulb />
  },
  generate_podcast: {
    displayName: 'Generating podcast content',
    icon: <Headphones />
  },
  store_data: {
    displayName: 'Storing report data',
    icon: <Storage />
  },
  sending_notifications: {
    displayName: 'Sending email notifications',
    icon: <Email />
  },
  submitted_for_indexing: {
    displayName: 'Submitted for indexing',
    icon: <Search />
  }
};

export const ProcessingStatusDialog: React.FC<ProcessingStatusDialogProps> = ({
  open,
  operationId,
  onClose,
  onComplete
}) => {
  const { authenticatedFetch } = useAuthenticatedFetch();
  const api = createApiClient(authenticatedFetch);
  const [showDetails, setShowDetails] = useState(false);
  const [hasCompleted, setHasCompleted] = useState(false);

  const { data: operation, error } = useQuery({
    queryKey: ['operation-status', operationId],
    queryFn: async () => {
      const response = await api.get(`/api/marketcompass/admin/operations/${operationId}`);
      return response.data;
    },
    refetchInterval: (query) => {
      if (query.state.data?.status === 'completed' || query.state.data?.status === 'failed') {
        return false;
      }
      return 1000; // Poll every second while processing
    },
    enabled: open && !!operationId
  });

  useEffect(() => {
    if (operation?.status === 'completed' && !hasCompleted) {
      setHasCompleted(true);
      onComplete(); // Notify parent that processing is complete, but don't close
    }
  }, [operation?.status, hasCompleted, onComplete]);

  const getSteps = (): ProcessingStep[] => {
    const steps: ProcessingStep[] = [];

    (Object.entries(stepConfig) as Array<[StepName, typeof stepConfig[StepName]]>).forEach(([key, config]) => {
      let status: ProcessingStep['status'] = 'pending';

      if (operation) {
        // If operation is completed, mark all steps as completed
        if (operation.status === 'completed') {
          status = 'completed';
        } else if (operation.status === 'failed') {
          // If operation failed, mark current step as failed
          if (operation.currentStep === key) {
            status = 'failed';
          } else {
            // Steps after the failed one remain pending
            const stepOrder = Object.keys(stepConfig) as StepName[];
            const currentIndex = stepOrder.indexOf(operation.currentStep as StepName || '' as StepName);
            const thisIndex = stepOrder.indexOf(key);
            status = thisIndex < currentIndex ? 'completed' : 'pending';
          }
        } else if (operation.currentStep === key) {
          status = 'processing';
        } else {
          // Determine if this step is complete based on order
          const stepOrder = Object.keys(stepConfig) as StepName[];
          const currentIndex = stepOrder.indexOf(operation.currentStep as StepName || '' as StepName);
          const thisIndex = stepOrder.indexOf(key);
          status = thisIndex < currentIndex ? 'completed' : 'pending';
        }
      }

      steps.push({
        name: key,
        displayName: config.displayName,
        icon: config.icon,
        status,
        error: operation?.error && operation.currentStep === key ? operation.error : undefined
      });
    });

    return steps;
  };

  const getStepIcon = (step: ProcessingStep) => {
    switch (step.status) {
      case 'completed':
        return <CheckCircle color="success" />;
      case 'processing':
        return <CircularProgress size={24} />;
      case 'failed':
        return <Error color="error" />;
      default:
        return <RadioButtonUnchecked color="disabled" />;
    }
  };

  const steps = getSteps();
  const isProcessing = operation?.status === 'processing';
  const isCompleted = operation?.status === 'completed';
  const isFailed = operation?.status === 'failed';

  return (
    <Dialog
      open={open}
      onClose={isFailed || isCompleted ? onClose : undefined}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown={isProcessing}
    >
      <DialogTitle>
        {isCompleted
          ? 'Processing Complete'
          : isFailed
          ? 'Processing Failed'
          : 'Processing Report'}
      </DialogTitle>
      
      <DialogContent>
        <Box sx={{ py: 2 }}>
          {/* Overall progress */}
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" color="text.secondary">
                {isCompleted
                  ? 'All steps completed successfully'
                  : isFailed
                  ? 'Processing failed'
                  : `Progress: ${operation?.progress || 0}%`}
              </Typography>
              {!isCompleted && !isFailed && (
                <Typography variant="body2" color="text.secondary">
                  {operation?.currentStep && operation.currentStep in stepConfig 
                    ? stepConfig[operation.currentStep as StepName].displayName 
                    : operation?.currentStep === 'completed' || operation?.currentStep === 'completed_with_notification_error'
                    ? 'Finalizing...'
                    : 'Initializing...'}
                </Typography>
              )}
            </Box>
            <LinearProgress
              variant="determinate"
              value={operation?.progress || 0}
              color={isCompleted ? 'success' : isFailed ? 'error' : 'primary'}
              sx={{ height: 8, borderRadius: 1 }}
            />
          </Box>

          {/* Step list */}
          <List>
            {steps.map((step, index) => (
              <ListItem key={step.name} sx={{ py: 0.5 }}>
                <ListItemIcon sx={{ minWidth: 40 }}>
                  {getStepIcon(step)}
                </ListItemIcon>
                <ListItemText
                  primary={step.displayName}
                  secondary={step.error}
                  primaryTypographyProps={{
                    color: step.status === 'failed' ? 'error' : 'textPrimary'
                  }}
                />
              </ListItem>
            ))}
          </List>

          {/* Error details */}
          {isFailed && operation?.error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              <Typography variant="body2">{operation.error}</Typography>
            </Alert>
          )}

          {/* Success message */}
          {isCompleted && operation?.result && (
            <Alert 
              severity={operation.result.notificationsSent === false ? "warning" : "success"} 
              sx={{ mt: 2 }}
            >
              <Typography variant="body2" gutterBottom>
                Report processed successfully!
                {operation.result.notificationsSent === false && ' (Notifications failed)'}
              </Typography>
              <Box sx={{ mt: 1 }}>
                <Button
                  size="small"
                  startIcon={showDetails ? <ExpandLess /> : <ExpandMore />}
                  onClick={() => setShowDetails(!showDetails)}
                >
                  {showDetails ? 'Hide' : 'Show'} Details
                </Button>
                <Collapse in={showDetails}>
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="caption" display="block">
                      Report ID: {operation.result.reportId}
                    </Typography>
                    {operation.result.podcastId && (
                      <Typography variant="caption" display="block">
                        Podcast ID: {operation.result.podcastId}
                      </Typography>
                    )}
                    <Typography variant="caption" display="block">
                      Key Findings: {operation.result.keyFindingsCount}
                    </Typography>
                    <Typography variant="caption" display="block">
                      Processing Time: {(operation.result.processingTime / 1000).toFixed(1)}s
                    </Typography>
                    {operation.result.notificationsSent !== undefined && (
                      <Typography variant="caption" display="block">
                        Email Notifications: {operation.result.notificationsSent ? 'Sent successfully' : 'Failed to send'}
                      </Typography>
                    )}
                    {operation.result.notificationError && (
                      <Typography variant="caption" display="block" color="error">
                        Notification Error: {operation.result.notificationError}
                      </Typography>
                    )}
                  </Box>
                </Collapse>
              </Box>
            </Alert>
          )}

          {/* Action buttons */}
          {(isFailed || isCompleted) && (
            <Box sx={{ mt: 3, textAlign: 'right' }}>
              <Button onClick={onClose} variant="contained">
                Close
              </Button>
            </Box>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
};